import { useMemo } from 'react';
import {
  ADMIN,
  FIRM,
  FIRM_ADMIN_VALUE,
  VIEW_ONLY_NO_ACTIONS_ROLES_VALUES,
} from 'common/constants/user';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import type { FirmScopedPermissions, UseFirmScopedPermissionsParams } from './useFirmScopedPermissions.types';

const hasFirmAdminPermission = (firmId: number | null | undefined, firmList: UseStoreValues[0]['firmList']) => {
  if (!firmId || !Array.isArray(firmList)) {
    return false;
  }

  const activeFirm = firmList.find(firm => Number(firm.id) === Number(firmId));
  return (
    activeFirm?.user_permissions?.some(
      permission =>
        permission.access_type === ADMIN &&
        permission.feature_object?.object_type === FIRM &&
        Number(permission.feature_object?.object_id) === Number(firmId)
    ) ?? false
  );
};

const useFirmScopedPermissions = ({
  firmId,
  isRestrictedUser,
  user,
}: UseFirmScopedPermissionsParams = {}): FirmScopedPermissions => {
  const [storeValue] = useStore() as UseStoreValues;
  const resolvedFirmId = firmId ?? storeValue.firmId;
  const resolvedIsRestrictedUser = Boolean(isRestrictedUser ?? storeValue.isRestrictedUser);
  const resolvedUser = user ?? storeValue.user;
  const isSuperuser = Boolean(resolvedUser?.is_superuser);

  // Keep firm-role lookup centralized so Main and Prism navigation gates do not drift.
  const currentFirmRole = useMemo(() => {
    const roleFromUserPermissions =
      resolvedUser?.firms_permissions?.find(({ id }) => Number(id) === Number(resolvedFirmId))?.user_firm_role ??
      null;

    if (roleFromUserPermissions !== null && roleFromUserPermissions !== undefined) {
      return roleFromUserPermissions;
    }

    if (hasFirmAdminPermission(resolvedFirmId, storeValue.firmList)) {
      return FIRM_ADMIN_VALUE;
    }

    return null;
  }, [resolvedFirmId, resolvedUser?.firms_permissions, storeValue.firmList]);

  const isViewOnlyUser = useMemo(
    () => currentFirmRole !== null && VIEW_ONLY_NO_ACTIONS_ROLES_VALUES.includes(currentFirmRole),
    [currentFirmRole]
  );
  // Unresolved firm roles should not briefly expose firm-level mutating actions while auth state hydrates.
  const canMutateFirmScopedEntities =
    isSuperuser || (!resolvedIsRestrictedUser && currentFirmRole !== null && !isViewOnlyUser);

  return {
    canCreateCompany: canMutateFirmScopedEntities,
    canDeleteCompany: canMutateFirmScopedEntities,
    canEditCompany: canMutateFirmScopedEntities,
    canCreateFund: canMutateFirmScopedEntities,
    canEditFund: canMutateFirmScopedEntities,
    canCreateMeasurementDate: canMutateFirmScopedEntities,
    canDeleteMeasurementDate: canMutateFirmScopedEntities,
    currentFirmRole,
    isRestrictedUser: resolvedIsRestrictedUser,
    isViewOnlyUser,
  };
};

export default useFirmScopedPermissions;
